const chapter = {
  id: 'binomial-theorem',
  slug: 'binomial-theorem',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Binomial Theorem',
        hindi: 'द्विपद प्रमेय',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '1–2 questions in JEE Main, often 1 in Advanced',
        hindi: 'जेईई मेन में 1-2 प्रश्न, एडवांस में अक्सर 1',
  },
  syllabusTags: ['Algebra', 'JEE-Main', 'JEE-Advanced', 'Class-11'],
  sections: [
    {
      id: 'intro',
      title: {
        english: 'Statement of the Theorem',
        hindi: 'प्रमेय का कथन',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Direct expansion of $(a+b)^2$ and $(a+b)^3$ is easy; for higher powers we need a systematic formula. The **Binomial Theorem** gives the expansion of $(a + b)^n$ for any positive integer $n$.',
        hindi: '$(a+b)^2$ और $(a+b)^3$ का सीधा विस्तार आसान है; उच्च शक्तियों के लिए हमें एक व्यवस्थित सूत्र की आवश्यकता है। **द्विपद प्रमेय** किसी भी सकारात्मक पूर्णांक $n$ के लिए $(a + b)^n$ का विस्तार देता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Binomial Theorem (positive integral index)',
        hindi: 'द्विपद प्रमेय (सकारात्मक अभिन्न सूचकांक)',
          },
          statement: {
            english: '$$\\,(a + b)^n = \\sum_{r=0}^{n} \\binom{n}{r} a^{n-r} b^r = \\binom{n}{0}a^n + \\binom{n}{1}a^{n-1}b + \\binom{n}{2}a^{n-2}b^2 + \\ldots + \\binom{n}{n}b^n,\\,$$ where $\\binom{n}{r} = \\dfrac{n!}{r!(n-r)!}$ are **binomial coefficients**.',
        hindi: '$\\,(a + b)^n = \\sum_{r=0}^{n} \\binom{n}{r} a^{n-r} b^r = \\binom{n}{0}a^n + \\binom{n}{1}a^{n-1}b + \\binom{n}{2}a^{n-2}b^2 + \\ldots + \\binom{n}{n}b^n,\\,$ जहां $\\binom{n}{r} = \\dfrac{n!}{r!(n-r)!}$ **द्विपद गुणांक** हैं।',
          },
          proof: {
            english: 'Induction on $n$. Base case $n = 1$ is trivial. Assume true for $n$; multiply both sides by $(a+b)$ and use $\\binom{n}{r-1} + \\binom{n}{r} = \\binom{n+1}{r}$ (Pascal\'s rule) to obtain the result for $n+1$.',
        hindi: '$n$ पर प्रेरण। आधार मामला $n = 1$ तुच्छ है। $n$ के लिए सत्य मानें; दोनों पक्षों को $(a+b)$ से गुणा करें और $n+1$ का परिणाम प्राप्त करने के लिए $\\binom{n}{r-1} + \\binom{n}{r} = \\binom{n+1}{r}$ (पास्कल का नियम) का उपयोग करें।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Expansion contains $n + 1$ terms.',
        hindi: 'विस्तार में $n + 1$ पद शामिल हैं।',
            },
            {
              english: 'Sum of indices of $a$ and $b$ in each term is $n$.',
        hindi: 'प्रत्येक पद में $a$ और $b$ के सूचकांकों का योग $n$ है।',
            },
            {
              english: 'Coefficients are symmetric: $\\binom{n}{r} = \\binom{n}{n-r}$.',
        hindi: 'गुणांक सममित हैं: $\\binom{n}{r} = \\binom{n}{n-r}$।',
            },
          ],
        },
      ],
    },
    {
      id: 'pascal',
      title: {
        english: 'Pascal\'s Triangle',
        hindi: 'पास्कल का त्रिभुज',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Binomial coefficients arrange beautifully into **Pascal\'s triangle**: each entry is the sum of the two entries directly above it (Pascal\'s rule).',
        hindi: 'द्विपद गुणांक **पास्कल के त्रिकोण** में खूबसूरती से व्यवस्थित होते हैं: प्रत्येक प्रविष्टि इसके ठीक ऊपर की दो प्रविष्टियों का योग है (पास्कल का नियम)।',
          },
        },
        {
          type: 'figure',
          svg: '<svg viewBox="0 0 460 260" xmlns="http://www.w3.org/2000/svg"><circle cx="230" cy="20" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="230" y="24" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><circle cx="211" cy="52" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="211" y="56" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="230" y1="34" x2="221" y2="40" stroke="#9ca3af" stroke-width="1"/><circle cx="249" cy="52" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="249" y="56" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="230" y1="34" x2="239" y2="40" stroke="#9ca3af" stroke-width="1"/><circle cx="192" cy="84" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="192" y="88" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="211" y1="66" x2="202" y2="72" stroke="#9ca3af" stroke-width="1"/><circle cx="230" cy="84" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="230" y="88" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">2</text><line x1="211" y1="66" x2="220" y2="72" stroke="#9ca3af" stroke-width="1"/><line x1="249" y1="66" x2="240" y2="72" stroke="#9ca3af" stroke-width="1"/><circle cx="268" cy="84" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="268" y="88" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="249" y1="66" x2="258" y2="72" stroke="#9ca3af" stroke-width="1"/><circle cx="173" cy="116" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="173" y="120" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="192" y1="98" x2="183" y2="104" stroke="#9ca3af" stroke-width="1"/><circle cx="211" cy="116" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="211" y="120" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">3</text><line x1="192" y1="98" x2="201" y2="104" stroke="#9ca3af" stroke-width="1"/><line x1="230" y1="98" x2="221" y2="104" stroke="#9ca3af" stroke-width="1"/><circle cx="249" cy="116" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="249" y="120" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">3</text><line x1="230" y1="98" x2="239" y2="104" stroke="#9ca3af" stroke-width="1"/><line x1="268" y1="98" x2="259" y2="104" stroke="#9ca3af" stroke-width="1"/><circle cx="287" cy="116" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="287" y="120" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="268" y1="98" x2="277" y2="104" stroke="#9ca3af" stroke-width="1"/><circle cx="154" cy="148" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="154" y="152" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="173" y1="130" x2="164" y2="136" stroke="#9ca3af" stroke-width="1"/><circle cx="192" cy="148" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="192" y="152" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">4</text><line x1="173" y1="130" x2="182" y2="136" stroke="#9ca3af" stroke-width="1"/><line x1="211" y1="130" x2="202" y2="136" stroke="#9ca3af" stroke-width="1"/><circle cx="230" cy="148" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="230" y="152" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">6</text><line x1="211" y1="130" x2="220" y2="136" stroke="#9ca3af" stroke-width="1"/><line x1="249" y1="130" x2="240" y2="136" stroke="#9ca3af" stroke-width="1"/><circle cx="268" cy="148" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="268" y="152" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">4</text><line x1="249" y1="130" x2="258" y2="136" stroke="#9ca3af" stroke-width="1"/><line x1="287" y1="130" x2="278" y2="136" stroke="#9ca3af" stroke-width="1"/><circle cx="306" cy="148" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="306" y="152" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="287" y1="130" x2="296" y2="136" stroke="#9ca3af" stroke-width="1"/><circle cx="135" cy="180" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="135" y="184" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="154" y1="162" x2="145" y2="168" stroke="#9ca3af" stroke-width="1"/><circle cx="173" cy="180" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="173" y="184" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">5</text><line x1="154" y1="162" x2="163" y2="168" stroke="#9ca3af" stroke-width="1"/><line x1="192" y1="162" x2="183" y2="168" stroke="#9ca3af" stroke-width="1"/><circle cx="211" cy="180" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="211" y="184" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">10</text><line x1="192" y1="162" x2="201" y2="168" stroke="#9ca3af" stroke-width="1"/><line x1="230" y1="162" x2="221" y2="168" stroke="#9ca3af" stroke-width="1"/><circle cx="249" cy="180" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="249" y="184" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">10</text><line x1="230" y1="162" x2="239" y2="168" stroke="#9ca3af" stroke-width="1"/><line x1="268" y1="162" x2="259" y2="168" stroke="#9ca3af" stroke-width="1"/><circle cx="287" cy="180" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="287" y="184" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">5</text><line x1="268" y1="162" x2="277" y2="168" stroke="#9ca3af" stroke-width="1"/><line x1="306" y1="162" x2="297" y2="168" stroke="#9ca3af" stroke-width="1"/><circle cx="325" cy="180" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="325" y="184" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="306" y1="162" x2="315" y2="168" stroke="#9ca3af" stroke-width="1"/><circle cx="116" cy="212" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="116" y="216" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="135" y1="194" x2="126" y2="200" stroke="#9ca3af" stroke-width="1"/><circle cx="154" cy="212" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="154" y="216" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">6</text><line x1="135" y1="194" x2="144" y2="200" stroke="#9ca3af" stroke-width="1"/><line x1="173" y1="194" x2="164" y2="200" stroke="#9ca3af" stroke-width="1"/><circle cx="192" cy="212" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="192" y="216" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">15</text><line x1="173" y1="194" x2="182" y2="200" stroke="#9ca3af" stroke-width="1"/><line x1="211" y1="194" x2="202" y2="200" stroke="#9ca3af" stroke-width="1"/><circle cx="230" cy="212" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="230" y="216" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">20</text><line x1="211" y1="194" x2="220" y2="200" stroke="#9ca3af" stroke-width="1"/><line x1="249" y1="194" x2="240" y2="200" stroke="#9ca3af" stroke-width="1"/><circle cx="268" cy="212" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="268" y="216" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">15</text><line x1="249" y1="194" x2="258" y2="200" stroke="#9ca3af" stroke-width="1"/><line x1="287" y1="194" x2="278" y2="200" stroke="#9ca3af" stroke-width="1"/><circle cx="306" cy="212" r="14" fill="#fef3c7" stroke="#374151" stroke-width="1"/><text x="306" y="216" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">6</text><line x1="287" y1="194" x2="296" y2="200" stroke="#9ca3af" stroke-width="1"/><line x1="325" y1="194" x2="316" y2="200" stroke="#9ca3af" stroke-width="1"/><circle cx="344" cy="212" r="14" fill="#e0e7ff" stroke="#374151" stroke-width="1"/><text x="344" y="216" font-size="12" text-anchor="middle" fill="#1f2937" font-weight="600">1</text><line x1="325" y1="194" x2="334" y2="200" stroke="#9ca3af" stroke-width="1"/></svg>',
          caption: {
            english: 'Pascal\'s triangle. Row n (starting from 0) gives the coefficients of (a + b)ⁿ.',
        hindi: 'पास्कल का त्रिकोण. पंक्ति n (0 से शुरू होकर) (a + b)ⁿ का गुणांक देती है।',
          },
        },
        {
          type: 'equation',
          latex: '\\binom{n}{r-1} + \\binom{n}{r} = \\binom{n+1}{r}',
          label: {
            english: 'Pascal\'s identity',
        hindi: 'पास्कल की पहचान',
          },
        },
      ],
    },
    {
      id: 'general-term',
      title: {
        english: 'General Term & Special Terms',
        hindi: 'व्यापक पद और विशेष पद',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'General term',
        hindi: 'सामान्य शब्द',
          },
          body: {
            english: 'In the expansion of $(a+b)^n$, the **(r+1)-th term** is $T_{r+1} = \\binom{n}{r} a^{n-r} b^r$.',
        hindi: '$(a+b)^n$ के विस्तार में, **(r+1)-वाँ पद** $T_{r+1} = \\binom{n}{r} a^{n-r} b^r$ है।',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'When asked for the **5th term**, set $r + 1 = 5 \\Rightarrow r = 4$. **Always be careful**: $T_{r+1}$ uses $r$, not $r-1$.',
        hindi: 'जब **5वाँ पद** माँगा जाए, तो $r + 1 = 5 \\Rightarrow r = 4$ सेट करें। **हमेशा सावधान रहें**: $T_{r+1}$ $r$ का उपयोग करता है, $r-1$ का नहीं।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Middle term(s)',
        hindi: 'मध्य पद',
          },
          body: {
            english: `For $(a+b)^n$:
• If $n$ is **even**, there is one middle term: $T_{n/2 + 1}$.
• If $n$ is **odd**, two middle terms: $T_{(n+1)/2}$ and $T_{(n+3)/2}$.`,
            hindi: '$(a+b)^n$ के लिए: • यदि $n$ **सम** है, तो एक मध्य पद है: $T_{n/2 + 1}$। • यदि $n$ **विषम** है, तो दो मध्य पद: $T_{(n+1)/2}$ और $T_{(n+3)/2}$।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Term independent of $x$',
        hindi: '$x$ से स्वतंत्र शब्द',
          },
          body: {
            english: 'In an expansion involving $x$, set the exponent of $x$ in $T_{r+1}$ equal to $0$ and solve for $r$. Then $T_{r+1}$ is the required term.',
        hindi: '$x$ से जुड़े विस्तार में, $T_{r+1}$ में $x$ के घातांक को $0$ के बराबर सेट करें और $r$ के लिए हल करें। तब $T_{r+1}$ आवश्यक पद है।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find the term independent of $x$ in $\\left(x^2 - \\dfrac{1}{x}\\right)^{12}$.',
        hindi: '$\\left(x^2 - \\dfrac{1}{x}\\right)^{12}$ में $x$ से स्वतंत्र पद ज्ञात कीजिए।',
          },
          solution: {
            english: '$T_{r+1} = \\binom{12}{r}(x^2)^{12-r}(-1/x)^r = (-1)^r \\binom{12}{r} x^{24 - 2r - r} = (-1)^r \\binom{12}{r} x^{24 - 3r}$. Independent of $x$ ⇒ $24 - 3r = 0 \\Rightarrow r = 8$. Term $= \\binom{12}{8} = 495$.',
        hindi: '$T_{r+1} = \\binom{12}{r}(x^2)^{12-r}(-1/x)^r = (-1)^r \\binom{12}{r} x^{24 - 2r - r} = (-1)^r \\binom{12}{r} x^{24 - 3r}$. $x$ ⇒ $24 - 3r = 0 \\Rightarrow r = 8$ से स्वतंत्र. अवधि $= \\binom{12}{8} = 495$.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Find the middle term(s) of $(1 - 2x)^{10}$.',
        hindi: '$(1 - 2x)^{10}$ का मध्य पद ज्ञात कीजिए।',
          },
          solution: {
            english: '$n = 10$ even ⇒ one middle term, $T_6$. $T_6 = \\binom{10}{5}(-2x)^5 = -252 \\cdot 32 x^5 = -8064 x^5$.',
        hindi: '$n = 10$ सम ⇒ एक मध्य पद, $T_6$। $T_6 = \\binom{10}{5}(-2x)^5 = -252 \\cdot 32 x^5 = -8064 x^5$.',
          },
        },
      ],
    },
    {
      id: 'identities',
      title: {
        english: 'Key Coefficient Identities',
        hindi: 'मुख्य गुणांक सर्वसमिकाएँ',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Setting specific values of $a, b$ in $(a+b)^n$ gives many useful identities. Let $C_r = \\binom{n}{r}$.',
        hindi: '$(a+b)^n$ में $a, b$ के विशिष्ट मान सेट करने से कई उपयोगी पहचान मिलती हैं। चलो $C_r = \\binom{n}{r}$।',
          },
        },
        {
          type: 'table',
          headers: ['Identity', 'How to derive'],
          rows: [
            ['$C_0 + C_1 + C_2 + \\ldots + C_n = 2^n$', 'Put $a = b = 1$.'],
            ['$C_0 - C_1 + C_2 - \\ldots + (-1)^n C_n = 0$', 'Put $a = 1, b = -1$.'],
            ['$C_0 + C_2 + C_4 + \\ldots = 2^{n-1}$', 'Add the two identities above.'],
            ['$C_1 + C_3 + C_5 + \\ldots = 2^{n-1}$', 'Subtract.'],
            ['$C_0^2 + C_1^2 + \\ldots + C_n^2 = \\binom{2n}{n}$', 'Coefficient of $x^n$ in $(1+x)^n(1+x)^n$.'],
            ['$\\sum_{r=0}^{n} r\\,C_r = n \\cdot 2^{n-1}$', 'Differentiate $(1+x)^n$ and put $x = 1$.'],
            ['$\\sum_{r=0}^{n} \\dfrac{C_r}{r+1} = \\dfrac{2^{n+1} - 1}{n + 1}$', 'Integrate $(1+x)^n$ from $0$ to $1$.'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**Powerful trick**: To get sums like $\\sum r C_r$, **differentiate** $(1+x)^n$. To get $\\sum \\tfrac{C_r}{r+1}$, **integrate**. Multiplying by $x$ before differentiating handles $\\sum r^2 C_r$.',
        hindi: '**शक्तिशाली तरकीब**: $\\sum r C_r$ जैसी रकम पाने के लिए, **अंतर** $(1+x)^n$ करें। $\\sum \\tfrac{C_r}{r+1}$ प्राप्त करने के लिए, **एकीकृत** करें। हैंडल $\\sum r^2 C_r$ को अलग करने से पहले $x$ से गुणा करना।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Evaluate $\\sum_{r=0}^{n} (r+1) \\binom{n}{r}$.',
        hindi: '$\\sum_{r=0}^{n} (r+1) \\binom{n}{r}$ का मूल्यांकन करें.',
          },
          solution: {
            english: '$\\sum (r+1) C_r = \\sum r C_r + \\sum C_r = n \\cdot 2^{n-1} + 2^n = 2^{n-1}(n + 2)$.',
        hindi: '$\\sum (r+1) C_r = \\sum r C_r + \\sum C_r = n \\cdot 2^{n-1} + 2^n = 2^{n-1}(n + 2)$.',
          },
        },
      ],
    },
    {
      id: 'greatest-term-coeff',
      title: {
        english: 'Greatest Term & Greatest Coefficient',
        hindi: 'महत्तम पद और महत्तम गुणांक',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Greatest binomial coefficient',
        hindi: 'सबसे बड़ा द्विपद गुणांक',
          },
          statement: {
            english: `In the expansion of $(1+x)^n$, the greatest binomial coefficient is:
• $\\binom{n}{n/2}$ if $n$ is even.
• $\\binom{n}{(n-1)/2} = \\binom{n}{(n+1)/2}$ if $n$ is odd (two equal greatest).`,
            hindi: '$(1+x)^n$ के विस्तार में, सबसे बड़ा द्विपद गुणांक है: • $\\binom{n}{n/2}$ यदि $n$ सम है। • $\\binom{n}{(n-1)/2} = \\binom{n}{(n+1)/2}$ यदि $n$ विषम है (दो बराबर सबसे बड़े)।',
          },
        },
        {
          type: 'paragraph',
          text: {
            english: 'For the **greatest term** in $(a+b)^n$ for given numerical $a, b$: use the ratio of consecutive terms.',
        hindi: 'दिए गए संख्यात्मक $a, b$ के लिए $(a+b)^n$ में **सबसे बड़े पद** के लिए: लगातार पदों के अनुपात का उपयोग करें।',
          },
        },
        {
          type: 'equation',
          latex: '\\dfrac{T_{r+1}}{T_r} = \\dfrac{n - r + 1}{r}\\cdot\\dfrac{b}{a}',
        },
        {
          type: 'paragraph',
          text: {
            english: 'The terms increase while this ratio $> 1$ and decrease when $< 1$. Solve $\\tfrac{T_{r+1}}{T_r} \\ge 1$ to find the index of the greatest term.',
        hindi: 'यह अनुपात $> 1$ होने पर पद बढ़ते हैं और $< 1$ होने पर घटते हैं। सबसे बड़े पद का सूचकांक ज्ञात करने के लिए $\\tfrac{T_{r+1}}{T_r} \\ge 1$ को हल करें।',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find the greatest term in the expansion of $(2 + 3x)^{15}$ when $x = \\tfrac{1}{2}$.',
        hindi: 'जब $x = \\tfrac{1}{2}$ हो तो $(2 + 3x)^{15}$ के विस्तार में सबसे बड़ा पद ज्ञात कीजिए।',
          },
          solution: {
            english: '$\\dfrac{T_{r+1}}{T_r} = \\dfrac{16 - r}{r}\\cdot\\dfrac{3 \\cdot 1/2}{2} = \\dfrac{3(16 - r)}{4r}$. Set $\\ge 1$: $3(16 - r) \\ge 4r \\Rightarrow r \\le 48/7 \\approx 6.86$. So greatest term is $T_7$ ($r = 6$).',
        hindi: '$\\dfrac{T_{r+1}}{T_r} = \\dfrac{16 - r}{r}\\cdot\\dfrac{3 \\cdot 1/2}{2} = \\dfrac{3(16 - r)}{4r}$. $\\ge 1$: $3(16 - r) \\ge 4r \\Rightarrow r \\le 48/7 \\approx 6.86$ सेट करें। अतः सबसे बड़ा पद $T_7$ ($r = 6$) है।',
          },
        },
      ],
    },
    {
      id: 'multinomial',
      title: {
        english: 'Multinomial Theorem (brief)',
        hindi: 'बहुपद प्रमेय (संक्षिप्त)',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Multinomial Theorem',
        hindi: 'बहुपद प्रमेय',
          },
          statement: {
            english: '$(x_1 + x_2 + \\ldots + x_k)^n = \\sum \\dfrac{n!}{n_1!\\,n_2!\\cdots n_k!}\\, x_1^{n_1} x_2^{n_2} \\cdots x_k^{n_k}$, summed over all non-negative integer tuples $(n_1, \\ldots, n_k)$ with $n_1 + \\ldots + n_k = n$.',
        hindi: '$(x_1 + x_2 + \\ldots + x_k)^n = \\sum \\dfrac{n!}{n_1!\\,n_2!\\cdots n_k!}\\, x_1^{n_1} x_2^{n_2} \\cdots x_k^{n_k}$, सभी गैर-नकारात्मक पूर्णांक टुपल्स $(n_1, \\ldots, n_k)$ को $n_1 + \\ldots + n_k = n$ के साथ संक्षेपित किया गया है।',
          },
        },
        {
          type: 'paragraph',
          text: {
            english: 'Number of distinct terms in the expansion is $\\binom{n + k - 1}{k - 1}$.',
        hindi: 'विस्तार में विशिष्ट पदों की संख्या $\\binom{n + k - 1}{k - 1}$ है।',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Find the coefficient of $x^2 y^3 z$ in $(x + y + z)^6$.',
        hindi: '$(x + y + z)^6$ में $x^2 y^3 z$ का गुणांक ज्ञात कीजिए।',
          },
          solution: {
            english: '$\\dfrac{6!}{2!\\,3!\\,1!} = \\dfrac{720}{2 \\cdot 6 \\cdot 1} = 60$.',
        hindi: '$\\dfrac{6!}{2!\\,3!\\,1!} = \\dfrac{720}{2 \\cdot 6 \\cdot 1} = 60$.',
          },
        },
      ],
    },
    {
      id: 'binomial-any-index',
      title: {
        english: 'Binomial Series for Any Index',
        hindi: 'किसी भी घातांक के लिए द्विपद श्रेणी',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Binomial Series (any rational index)',
        hindi: 'द्विपद श्रृंखला (कोई भी तर्कसंगत सूचकांक)',
          },
          statement: {
            english: `For $|x| < 1$ and any real $n$:
$$(1 + x)^n = 1 + nx + \\dfrac{n(n-1)}{2!}x^2 + \\dfrac{n(n-1)(n-2)}{3!}x^3 + \\ldots$$`,
            hindi: '$|x| < 1$ और किसी भी वास्तविक $n$ के लिए: $$(1 + x)^n = 1 + nx + \\dfrac{n(n-1)}{2!}x^2 + \\dfrac{n(n-1)(n-2)}{3!}x^3 + \\ldots$$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'When $n$ is a positive integer, the series **terminates** in $n + 1$ terms.',
        hindi: 'जब $n$ एक धनात्मक पूर्णांक होता है, तो श्रृंखला $n + 1$ पदों में **समाप्त** हो जाती है।',
            },
            {
              english: 'For non-integer $n$, the series is **infinite** and converges only when $|x| < 1$.',
        hindi: 'गैर-पूर्णांक $n$ के लिए, श्रृंखला **अनंत** है और केवल $|x| < 1$ होने पर ही अभिसरित होती है।',
            },
            {
              english: 'The general term is $T_{r+1} = \\dfrac{n(n-1)(n-2)\\cdots(n-r+1)}{r!} x^r$.',
        hindi: 'सामान्य शब्द $T_{r+1} = \\dfrac{n(n-1)(n-2)\\cdots(n-r+1)}{r!} x^r$ है।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Series', 'Expansion (|x| < 1)'],
          rows: [
            ['$(1 + x)^{-1}$', '$1 - x + x^2 - x^3 + \\ldots$'],
            ['$(1 - x)^{-1}$', '$1 + x + x^2 + x^3 + \\ldots$'],
            ['$(1 + x)^{-2}$', '$1 - 2x + 3x^2 - 4x^3 + \\ldots$'],
            ['$(1 - x)^{-2}$', '$1 + 2x + 3x^2 + 4x^3 + \\ldots$'],
            ['$(1 + x)^{1/2}$', '$1 + \\tfrac{x}{2} - \\tfrac{x^2}{8} + \\tfrac{x^3}{16} - \\ldots$'],
            ['$(1 - x)^{-1/2}$', '$1 + \\tfrac{x}{2} + \\tfrac{3x^2}{8} + \\tfrac{5x^3}{16} + \\ldots$'],
          ],
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Approximate $\\sqrt{1.02}$ using binomial series up to $x^2$.',
        hindi: '$x^2$ तक द्विपद श्रृंखला का उपयोग करके अनुमानित $\\sqrt{1.02}$।',
          },
          solution: {
            english: '$\\sqrt{1.02} = (1 + 0.02)^{1/2} \\approx 1 + \\tfrac{0.02}{2} - \\tfrac{(0.02)^2}{8} = 1 + 0.01 - 0.00005 = 1.00995$.',
        hindi: '$\\sqrt{1.02} = (1 + 0.02)^{1/2} \\approx 1 + \\tfrac{0.02}{2} - \\tfrac{(0.02)^2}{8} = 1 + 0.01 - 0.00005 = 1.00995$.',
          },
        },
      ],
    },
    {
      id: 'applications',
      title: {
        english: 'Applications & Tricks',
        hindi: 'अनुप्रयोग और युक्तियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Finding remainders**: e.g. remainder of $7^{2025}$ on division by $5$ — write $7 = 5 + 2$ and expand.',
        hindi: '**शेषफल ढूँढना**: उदा. $7^{2025}$ को $5$ से विभाजित करने पर शेषफल - $7 = 5 + 2$ लिखें और विस्तृत करें।',
            },
            {
              english: '**Last digit problems**: similar idea, expand modulo $10$ or $100$.',
        hindi: '**अंतिम अंक की समस्याएं**: समान विचार, मॉड्यूलो $10$ या $100$ का विस्तार करें।',
            },
            {
              english: '**Approximations**: small powers of $(1 + x)$ for $|x|$ small.',
        hindi: '**अनुमान**: $|x|$ के लिए $(1 + x)$ की छोटी शक्तियाँ।',
            },
            {
              english: '**Number-theoretic identities**: integer / fractional part splits using $(\\sqrt 5 + 2)^n$ etc.',
        hindi: '**संख्या-सैद्धांतिक पहचान**: पूर्णांक/भिन्नात्मक भाग $(\\sqrt 5 + 2)^n$ आदि का उपयोग करके विभाजित होता है।',
            },
          ],
        },
        {
          type: 'example',
          number: 7,
          problem: {
            english: 'Find the remainder when $7^{103}$ is divided by $25$.',
        hindi: 'जब $7^{103}$ को $25$ से विभाजित किया जाता है तो शेषफल ज्ञात कीजिए।',
          },
          solution: {
            english: '$7^2 = 49 = 50 - 1$, so $7^{103} = 7 \\cdot (7^2)^{51} = 7 \\cdot (50 - 1)^{51}$. Expand $(50 - 1)^{51}$: all terms with $50^k\\,(k \\ge 1)$ are divisible by 25; the last term is $(-1)^{51} = -1$. So $7^{103} \\equiv 7 \\cdot (-1) \\equiv -7 \\equiv 18 \\pmod{25}$.',
        hindi: '$7^2 = 49 = 50 - 1$, इसलिए $7^{103} = 7 \\cdot (7^2)^{51} = 7 \\cdot (50 - 1)^{51}$. $(50 - 1)^{51}$ का विस्तार करें: $50^k\\,(k \\ge 1)$ वाले सभी पद 25 से विभाज्य हैं; अंतिम पद $(-1)^{51} = -1$ है। तो $7^{103} \\equiv 7 \\cdot (-1) \\equiv -7 \\equiv 18 \\pmod{25}$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '(a+b)^n = \\sum_{r=0}^{n} \\binom{n}{r} a^{n-r} b^r',
      desc: {
        english: 'Binomial expansion',
        hindi: 'द्विपद विस्तार',
      },
    },
    {
      latex: 'T_{r+1} = \\binom{n}{r} a^{n-r} b^r',
      desc: {
        english: 'General term',
        hindi: 'सामान्य शब्द',
      },
    },
    {
      latex: '\\binom{n}{r} + \\binom{n}{r-1} = \\binom{n+1}{r}',
      desc: {
        english: 'Pascal\'s rule',
        hindi: 'पास्कल का नियम',
      },
    },
    {
      latex: '\\sum_{r=0}^{n} \\binom{n}{r} = 2^n',
      desc: {
        english: 'Sum of all coefficients',
        hindi: 'सभी गुणांकों का योग',
      },
    },
    {
      latex: '\\sum_{r=0}^{n} \\binom{n}{r}^2 = \\binom{2n}{n}',
      desc: {
        english: 'Sum of squares of coefficients',
        hindi: 'गुणांकों के वर्गों का योग',
      },
    },
    {
      latex: '\\sum_{r=0}^{n} r\\binom{n}{r} = n \\cdot 2^{n-1}',
      desc: {
        english: 'Differentiate trick',
        hindi: 'भेद करने की युक्ति',
      },
    },
    {
      latex: '(1+x)^n = 1 + nx + \\tfrac{n(n-1)}{2!}x^2 + \\ldots,\\ |x|<1',
      desc: {
        english: 'Binomial series for any index',
        hindi: 'किसी भी सूचकांक के लिए द्विपद श्रृंखला',
      },
    },
    {
      latex: '\\binom{n}{r} = \\binom{n}{n-r}',
      desc: {
        english: 'Symmetry',
        hindi: 'समरूपता',
      },
    },
  ],
  pyqs: [
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'General term',
      problem: {
        english: 'The coefficient of $x^7$ in $(1 + x)^{10}(1 + x^2)^{5}$ is:',
        hindi: '$(1 + x)^{10}(1 + x^2)^{5}$ में $x^7$ का गुणांक है:',
      },
      answer: {
        english: '$540$',
        hindi: '$540$',
      },
      solution: {
        english: 'Pick $x^{7-2k}$ from $(1+x)^{10}$ and $x^{2k}$ from $(1+x^2)^5$. Sum: $\\binom{10}{7}\\binom{5}{0} + \\binom{10}{5}\\binom{5}{1} + \\binom{10}{3}\\binom{5}{2} + \\binom{10}{1}\\binom{5}{3}$. Compute case-wise.',
        hindi: '$(1+x)^{10}$ में से $x^{7-2k}$ और $(1+x^2)^5$ में से $x^{2k}$ चुनें। योग: $\\binom{10}{7}\\binom{5}{0} + \\binom{10} केस-वार गणना करें.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Middle term',
      problem: {
        english: 'The middle term of $\\left(\\dfrac{2x}{3} - \\dfrac{3}{2x}\\right)^{20}$ is:',
        hindi: '$\\left(\\dfrac{2x}{3} - \\dfrac{3}{2x}\\right)^{20}$ का मध्य पद है:',
      },
      answer: {
        english: '$T_{11} = -\\binom{20}{10}$',
        hindi: '$T_{11} = -\\binom{20}{10}$',
      },
      solution: {
        english: '$n = 20$ even ⇒ middle term $T_{11}$ ($r = 10$). $T_{11} = \\binom{20}{10}(2x/3)^{10}(-3/(2x))^{10} = (-1)^{10}\\binom{20}{10}(2/3)^{10}(3/2)^{10} = \\binom{20}{10}$.',
        hindi: '$n = 20$ सम है, इसलिए मध्य पद $T_{11}$ होगा ($r = 10$)। $T_{11} = \\binom{20}{10}(2x/3)^{10}(-3/(2x))^{10} = (-1)^{10}\\binom{20}{10}(2/3)^{10}(3/2)^{10} = \\binom{20}{10}$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Identity',
      problem: {
        english: 'If $\\sum_{r=0}^{15} \\binom{15}{r} 2^r = a$ and $\\sum_{r=0}^{15} (-1)^r \\binom{15}{r} 2^r = b$, find $a + b$.',
        hindi: 'यदि $\\sum_{r=0}^{15} \\binom{15}{r} 2^r = a$ और $\\sum_{r=0}^{15} (-1)^r \\binom{15}{r} 2^r = b$, तो $a + b$ खोजें।',
      },
      answer: {
        english: '$2 \\cdot 3^{15} \\cdot$ even-index part — specifically $2(1 + 4 + 16 + \\ldots)$. Standard answer: $2 \\cdot ?$',
        hindi: '$2 \\cdot 3^{15} \\cdot$ सम-सूचकांक भाग - विशेष रूप से $2(1 + 4 + 16 + \\ldots)$। मानक उत्तर: $2 \\cdot?$',
      },
      solution: {
        english: '$a = (1+2)^{15} = 3^{15}$ and $b = (1-2)^{15} = (-1)^{15} = -1$. So $a + b = 3^{15} - 1$.',
        hindi: '$a = (1+2)^{15} = 3^{15}$ और $b = (1-2)^{15} = (-1)^{15} = -1$. तो $a + b = 3^{15} - 1$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Independent term',
      problem: {
        english: 'The term independent of $x$ in $\\left(\\sqrt x + \\dfrac{1}{x^2}\\right)^{10}$ is:',
        hindi: '$\\left(\\sqrt x + \\dfrac{1}{x^2}\\right)^{10}$ में $x$ से स्वतंत्र पद है:',
      },
      answer: {
        english: '$\\binom{10}{2} = 45$',
        hindi: '$\\binom{10}{2} = 45$',
      },
      solution: {
        english: '$T_{r+1} = \\binom{10}{r} x^{(10-r)/2 - 2r}$. Set exponent $= 0$: $(10-r)/2 = 2r \\Rightarrow 10 - r = 4r \\Rightarrow r = 2$. Coefficient $= \\binom{10}{2} = 45$.',
        hindi: '$T_{r+1} = \\binom{10}{r} x^{(10-r)/2 - 2r}$. घातांक सेट करें $= 0$: $(10-r)/2 = 2r \\Rightarrow 10 - आर = 4आर \\Rightarrow आर = 2$। गुणांक $= \\binom{10}{2} = 45$.',
      },
    },
    {
      year: 2019,
      exam: 'JEE Advanced',
      tag: 'Sum',
      problem: {
        english: 'Find $\\sum_{k=0}^{n} \\binom{n}{k}^2 \\cdot k$.',
        hindi: '$\\sum_{k=0}^{n} \\binom{n}{k}^2 \\cdot k$ खोजें।',
      },
      answer: {
        english: '$\\dfrac{n}{2}\\binom{2n}{n}$',
        hindi: '$\\dfrac{n}{2}\\binom{2n}{n}$',
      },
      solution: {
        english: 'Use the integral representation or identity: $\\sum_{k=0}^{n} k\\binom{n}{k}\\binom{n}{n-k} = n\\binom{2n-1}{n-1} = \\dfrac{n}{2}\\binom{2n}{n}$.',
        hindi: 'अभिन्न प्रतिनिधित्व या पहचान का उपयोग करें: $\\sum_{k=0}^{n} k\\binom{n}{k}\\binom{n}{n-k} = n\\binom{2n-1}{n-1} = \\dfrac{n}{2}\\binom{2n}{n}$।',
      },
    },
  ],
  next: {
    slug: 'permutations-combinations',
    title: 'Permutations & Combinations',
  },
};

export default chapter;
